import { useEffect, useState, useCallback, useMemo, Fragment } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useActiveProperty } from "@/context/ActivePropertyContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { CalendarRange, ChevronLeft, ChevronRight, Printer, Copy, Loader2, Wand2, Settings2, X } from "lucide-react";
import "@/schedule-print.css";

const DOW = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const DEFAULT_STARTS = ["07:00", "15:00", "23:00", "09:00"];
const addHours = (hhmm, h) => {
  const [H, M] = hhmm.split(":").map(Number);
  const t = (((H * 60 + M + h * 60) % 1440) + 1440) % 1440;
  return `${String(Math.floor(t / 60)).padStart(2, "0")}:${String(t % 60).padStart(2, "0")}`;
};
const shortT = (hhmm) => {
  if (!hhmm) return "";
  const [H, M] = hhmm.split(":").map(Number);
  const ap = H >= 12 ? "p" : "a";
  const h12 = H % 12 === 0 ? 12 : H % 12;
  return M === 0 ? `${h12}${ap}` : `${h12}:${String(M).padStart(2, "0")}${ap}`;
};
// Build 8-hour shift presets from a property's configured start times (fallback to defaults).
const buildPresets = (starts) =>
  (starts && starts.length ? starts : DEFAULT_STARTS).map((s) => ({ start: s, end: addHours(s, 8), l: `${shortT(s)}–${shortT(addHours(s, 8))}` }));

const isoDate = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
function startOfWeek(dateStr) {
  const d = new Date(dateStr + "T00:00:00");
  d.setDate(d.getDate() - d.getDay());
  return isoDate(d);
}
function periodDates(startStr) {
  const out = [];
  const d = new Date(startStr + "T00:00:00");
  for (let i = 0; i < 14; i++) { out.push(isoDate(d)); d.setDate(d.getDate() + 1); }
  return out;
}
// Start of the 14-day window (aligned to the property's pay-period anchor) containing `todayIso`.
function alignedStart(anchorIso, todayIso) {
  const anchor = new Date(anchorIso + "T00:00:00");
  const today = new Date(todayIso + "T00:00:00");
  if (isNaN(anchor.getTime())) return startOfWeek(todayIso);
  const idx = Math.floor((today - anchor) / 86400000 / 14);
  const s = new Date(anchor);
  s.setDate(anchor.getDate() + idx * 14);
  return isoDate(s);
}
function fmt12(hhmm) {
  if (!hhmm) return "";
  const [h, m] = hhmm.split(":").map(Number);
  const ap = h >= 12 ? "p" : "a";
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12}:${String(m).padStart(2, "0")}${ap}`;
}
const cellLabel = (c) => {
  if (!c) return "";
  if (c.notes === "OFF") return "OFF";
  if (c.start && c.end) return `${fmt12(c.start)}–${fmt12(c.end)}`;
  return c.notes || "";
};
const fmtHdr = (iso) => new Date(iso + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric" });

function ScheduleCell({ value, onSave, onClear, testid, presets }) {
  const [open, setOpen] = useState(false);
  const [s, setS] = useState("");
  const [e, setE] = useState("");
  useEffect(() => { if (open) { setS(value?.start || ""); setE(value?.end || ""); } }, [open, value]);
  const off = value?.notes === "OFF";
  const label = cellLabel(value);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          data-testid={testid}
          className={`sched-cell-trigger flex h-9 w-full items-center justify-center rounded-md border px-1 text-xs font-medium transition-colors ${
            off ? "border-zinc-300 bg-zinc-100 text-zinc-500 dark:border-zinc-700 dark:bg-zinc-800"
            : label ? "border-primary/30 bg-primary/10 text-foreground"
            : "border-dashed border-border text-muted-foreground hover:border-primary/40"
          }`}
        >
          {label || "+"}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-56 p-3" align="center">
        <div className="mb-2 grid grid-cols-2 gap-1.5">
          {presets.map((p) => (
            <Button key={p.l} variant="outline" size="sm" className="h-8"
              data-testid={`${testid}-preset-${p.l}`}
              onClick={() => { onSave({ start: p.start, end: p.end, notes: "" }); setOpen(false); }}>
              {p.l}
            </Button>
          ))}
        </div>
        <div className="mb-2 flex items-end gap-2">
          <div className="flex-1">
            <div className="mb-0.5 text-[0.6rem] uppercase text-muted-foreground">Start</div>
            <Input type="time" value={s} onChange={(ev) => setS(ev.target.value)} className="h-8" data-testid={`${testid}-start`} />
          </div>
          <div className="flex-1">
            <div className="mb-0.5 text-[0.6rem] uppercase text-muted-foreground">End</div>
            <Input type="time" value={e} onChange={(ev) => setE(ev.target.value)} className="h-8" data-testid={`${testid}-end`} />
          </div>
        </div>
        <div className="flex items-center justify-between gap-1.5">
          <Button size="sm" className="h-8 flex-1" disabled={!s || !e}
            onClick={() => { onSave({ start: s, end: e, notes: "" }); setOpen(false); }}
            data-testid={`${testid}-save`}>Save</Button>
          <Button size="sm" variant="secondary" className="h-8"
            onClick={() => { onSave({ start: "", end: "", notes: "OFF" }); setOpen(false); }}
            data-testid={`${testid}-off`}>OFF</Button>
          <Button size="sm" variant="ghost" className="h-8 text-destructive"
            onClick={() => { onClear(); setOpen(false); }}
            data-testid={`${testid}-clear`}>Clear</Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}

function UsualShiftsDialog({ employee, onClose, onSaved, presets }) {
  const [tmpl, setTmpl] = useState({});
  const [saving, setSaving] = useState(false);
  useEffect(() => { setTmpl(employee?.default_shifts || {}); }, [employee]);
  const setDay = (wd, patch) => setTmpl((t) => ({ ...t, [wd]: { ...(t[wd] || {}), ...patch } }));
  const clearDay = (wd) => setTmpl((t) => { const n = { ...t }; delete n[wd]; return n; });
  const save = async () => {
    setSaving(true);
    const clean = {};
    Object.entries(tmpl).forEach(([wd, v]) => { if (v?.start && v?.end) clean[wd] = { start: v.start, end: v.end }; });
    try {
      await api.patch(`/employees/${employee.id}/default-shifts`, { default_shifts: clean });
      toast.success("Usual shifts saved");
      onSaved(employee.id, clean);
      onClose();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open={!!employee} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="usual-shifts-dialog">
        <DialogHeader>
          <DialogTitle>Usual shifts · {employee?.first_name} {employee?.last_name}</DialogTitle>
          <DialogDescription>Set the shift this person normally works each weekday. Leave a day blank if they don't usually work it. Then use "Apply Usual Shifts" to pre-fill a period — you only edit the exceptions.</DialogDescription>
        </DialogHeader>
        <div className="max-h-[55vh] space-y-2 overflow-y-auto pr-1">
          {DOW.map((label, wd) => {
            const v = tmpl[wd] || {};
            return (
              <div key={wd} className="flex flex-wrap items-center gap-2" data-testid={`usual-row-${wd}`}>
                <div className="w-10 text-sm font-bold">{label}</div>
                <div className="flex gap-1">
                  {presets.map((p) => (
                    <Button key={p.l} type="button" variant="outline" size="sm" className="h-8 px-2 text-xs"
                      onClick={() => setDay(wd, { start: p.start, end: p.end })} data-testid={`usual-${wd}-preset-${p.l}`}>{p.l}</Button>
                  ))}
                </div>
                <Input type="time" value={v.start || ""} onChange={(e) => setDay(wd, { start: e.target.value })} className="h-8 w-28" data-testid={`usual-${wd}-start`} />
                <span className="text-muted-foreground">–</span>
                <Input type="time" value={v.end || ""} onChange={(e) => setDay(wd, { end: e.target.value })} className="h-8 w-28" data-testid={`usual-${wd}-end`} />
                <Button type="button" variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => clearDay(wd)} data-testid={`usual-${wd}-clear`}><X className="h-4 w-4" /></Button>
              </div>
            );
          })}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="usual-save-btn">{saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Save Usual Shifts</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Schedule() {
  const { activePropertyId: propertyId } = useActiveProperty();
  const [props, setProps] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [periodStart, setPeriodStart] = useState(() => startOfWeek(isoDate(new Date())));
  const [shifts, setShifts] = useState({});
  const [copying, setCopying] = useState(false);
  const [applying, setApplying] = useState(false);
  const [tmplEmp, setTmplEmp] = useState(null);

  const days = useMemo(() => periodDates(periodStart), [periodStart]);
  const property = props.find((p) => p.id === propertyId);
  const presets = useMemo(() => buildPresets(property?.shift_start_times), [property]);

  useEffect(() => { api.get("/properties").then((r) => setProps(r.data)).catch(() => {}); }, []);

  // Align the 2-week window to the property's pay-period start date (anchor).
  useEffect(() => {
    const p = props.find((x) => x.id === propertyId);
    if (p && p.payroll_anchor) setPeriodStart(alignedStart(p.payroll_anchor, isoDate(new Date())));
  }, [propertyId, props]);

  useEffect(() => {
    if (!propertyId) return;
    api.get("/employees", { params: { property_id: propertyId } })
      .then((r) => setEmployees([...r.data].sort((a, b) =>
        (a.department || "").localeCompare(b.department || "") || (a.last_name || "").localeCompare(b.last_name || ""))))
      .catch(() => setEmployees([]));
  }, [propertyId]);

  const loadShifts = useCallback(() => {
    if (!propertyId) return;
    api.get("/shifts", { params: { property_id: propertyId, start: days[0], end: days[13] } })
      .then((r) => {
        const m = {};
        r.data.forEach((s) => { m[`${s.employee_id}|${s.date}`] = { start: s.start_time, end: s.end_time, notes: s.notes || "" }; });
        setShifts(m);
      })
      .catch(() => setShifts({}));
  }, [propertyId, days]);

  useEffect(() => { loadShifts(); }, [loadShifts]);

  const saveShift = async (eid, date, cell) => {
    setShifts((m) => ({ ...m, [`${eid}|${date}`]: cell }));
    try {
      await api.post("/shifts", { property_id: propertyId, employee_id: eid, date, start_time: cell.start || "", end_time: cell.end || "", notes: cell.notes || "" });
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const clearShift = async (eid, date) => {
    setShifts((m) => { const n = { ...m }; delete n[`${eid}|${date}`]; return n; });
    try { await api.post("/shifts", { property_id: propertyId, employee_id: eid, date, start_time: "", end_time: "", notes: "" }); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const copyWeek = async () => {
    setCopying(true);
    try {
      const jobs = [];
      employees.forEach((emp) => {
        for (let i = 0; i < 7; i++) {
          const src = shifts[`${emp.id}|${days[i]}`];
          if (src && (src.start || src.end || src.notes)) {
            jobs.push(api.post("/shifts", { property_id: propertyId, employee_id: emp.id, date: days[i + 7], start_time: src.start || "", end_time: src.end || "", notes: src.notes || "" }));
          }
        }
      });
      await Promise.all(jobs);
      toast.success("Copied Week 1 → Week 2");
      loadShifts();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setCopying(false); }
  };

  const shiftPeriod = (dir) => {
    const d = new Date(periodStart + "T00:00:00");
    d.setDate(d.getDate() + dir * 14);
    setPeriodStart(isoDate(d));
  };

  const onTemplateSaved = (eid, def) =>
    setEmployees((list) => list.map((e) => (e.id === eid ? { ...e, default_shifts: def } : e)));

  const applyUsual = async () => {
    setApplying(true);
    try {
      const jobs = [];
      employees.forEach((emp) => {
        const tmpl = emp.default_shifts || {};
        days.forEach((d) => {
          const wd = new Date(d + "T00:00:00").getDay();
          const t = tmpl[String(wd)];
          const existing = shifts[`${emp.id}|${d}`];
          const filled = existing && (existing.start || existing.end || existing.notes);
          if (t && t.start && t.end && !filled) {
            jobs.push(api.post("/shifts", { property_id: propertyId, employee_id: emp.id, date: d, start_time: t.start, end_time: t.end, notes: "" }));
          }
        });
      });
      if (!jobs.length) {
        toast.info("Nothing to fill — set usual shifts (gear icon by each name), or these days are already scheduled.");
        return;
      }
      await Promise.all(jobs);
      toast.success(`Filled ${jobs.length} shift${jobs.length > 1 ? "s" : ""} from usual schedules`);
      loadShifts();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setApplying(false); }
  };

  const grouped = useMemo(() => {
    const g = {};
    employees.forEach((e) => { (g[e.department || "Staff"] ||= []).push(e); });
    return Object.entries(g);
  }, [employees]);

  return (
    <div className="space-y-6" data-testid="schedule-page">
      <div className="flex flex-wrap items-end justify-between gap-4 no-print">
        <div>
          <h1 className="font-heading text-3xl font-black tracking-tight flex items-center gap-2">
            <CalendarRange className="h-7 w-7 text-primary" /> Schedule
          </h1>
          <p className="text-sm text-muted-foreground">Build a 2-week schedule, then print it for the wall. Tap any day to set a shift or mark OFF.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="outline" size="icon" onClick={() => shiftPeriod(-1)} data-testid="sched-prev"><ChevronLeft className="h-4 w-4" /></Button>
          <div className="min-w-48 text-center text-sm font-bold" data-testid="sched-range">{fmtHdr(days[0])} – {fmtHdr(days[13])}</div>
          <Button variant="outline" size="icon" onClick={() => shiftPeriod(1)} data-testid="sched-next"><ChevronRight className="h-4 w-4" /></Button>
          <Button variant="outline" onClick={applyUsual} disabled={applying || !employees.length} data-testid="sched-apply-usual-btn">
            {applying ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Wand2 className="mr-2 h-4 w-4" />} Apply Usual Shifts
          </Button>
          <Button variant="outline" onClick={copyWeek} disabled={copying || !employees.length} data-testid="sched-copy-btn">
            {copying ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Copy className="mr-2 h-4 w-4" />} Copy Wk1 → Wk2
          </Button>
          <Button onClick={() => window.print()} disabled={!employees.length} data-testid="sched-print-btn"><Printer className="mr-2 h-4 w-4" /> Print</Button>
        </div>
      </div>

      {!propertyId ? (
        <div className="rounded-lg border border-dashed p-10 text-center text-sm text-muted-foreground">Select a property from the top bar.</div>
      ) : employees.length === 0 ? (
        <div className="rounded-lg border border-dashed p-10 text-center text-sm text-muted-foreground" data-testid="sched-empty">No employees for this property yet.</div>
      ) : (
        <div className="schedule-print-area">
          <div className="mb-3 hidden print:block">
            <div className="text-lg font-black">{property?.name}</div>
            <div className="text-sm">Schedule · {fmtHdr(days[0])} – {fmtHdr(days[13])}</div>
          </div>
          {[0, 7].map((weekOffset) => (
            <div key={weekOffset} className="mb-5 overflow-x-auto rounded-lg border border-border">
              <div className="bg-secondary px-3 py-1.5 text-xs font-bold uppercase tracking-wide text-muted-foreground">
                Week {weekOffset === 0 ? 1 : 2} · {fmtHdr(days[weekOffset])} – {fmtHdr(days[weekOffset + 6])}
              </div>
              <table className="w-full border-collapse text-sm">
                <thead>
                  <tr className="bg-secondary/60 text-center text-[0.6rem] uppercase text-muted-foreground">
                    <th className="sticky left-0 z-10 bg-secondary/60 p-2 text-left">Employee</th>
                    {days.slice(weekOffset, weekOffset + 7).map((d) => (
                      <th key={d} className="p-2">{DOW[new Date(d + "T00:00:00").getDay()]}<div className="font-normal normal-case">{fmtHdr(d)}</div></th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {grouped.map(([dept, emps]) => (
                    <Fragment key={`${dept}-${weekOffset}`}>
                      <tr className="bg-muted/40">
                        <td colSpan={8} className="px-2 py-1 text-[0.65rem] font-bold uppercase tracking-wide text-muted-foreground">{dept}</td>
                      </tr>
                      {emps.map((emp) => (
                        <tr key={emp.id} className="border-t border-border" data-testid={`sched-row-${emp.id}`}>
                          <td className="sticky left-0 z-10 whitespace-nowrap bg-card p-2 font-medium">
                            <span className="inline-flex items-center gap-1.5">
                              {emp.first_name} {emp.last_name}
                              {weekOffset === 0 && (
                                <button onClick={() => setTmplEmp(emp)} title="Set usual shifts" data-testid={`usual-open-${emp.id}`}
                                  className="no-print text-muted-foreground/60 transition-colors hover:text-primary">
                                  <Settings2 className="h-3.5 w-3.5" />
                                </button>
                              )}
                            </span>
                          </td>
                          {days.slice(weekOffset, weekOffset + 7).map((d) => (
                            <td key={d} className="p-1 text-center">
                              <ScheduleCell
                                value={shifts[`${emp.id}|${d}`]}
                                onSave={(cell) => saveShift(emp.id, d, cell)}
                                onClear={() => clearShift(emp.id, d)}
                                testid={`sched-cell-${emp.id}-${d}`}
                                presets={presets}
                              />
                            </td>
                          ))}
                        </tr>
                      ))}
                    </Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
        </div>
      )}
      {tmplEmp && <UsualShiftsDialog employee={tmplEmp} onClose={() => setTmplEmp(null)} onSaved={onTemplateSaved} presets={presets} />}
    </div>
  );
}
